import axios from 'axios';
import { plainToClass } from 'class-transformer';
import { Session } from 'auth/models/session';
import {
    CreateSalePropertyDto,
    MLNeighborhoodDto,
    PropertyPhotoDto,
    PublishListingDto,
    SaleListingDto,
    SalePropertyDto,
} from './sale.dto';
import { SalesService } from './sales.service';

const BASE = process.env.REACT_APP_API_BASE_URL;

export class ApiSalesService implements SalesService {
    constructor(private session: Session) {}

    private get headers() {
        return { Authorization: `Bearer ${this.session.getToken()}` };
    }

    async listSaleProperties(): Promise<SalePropertyDto[]> {
        const { data } = await axios.get(`${BASE}/sales`, { headers: this.headers });
        return data.map((item: any) => plainToClass(SalePropertyDto, item));
    }

    async getSaleProperty(propertyId: number): Promise<SalePropertyDto> {
        const { data } = await axios.get(`${BASE}/sales/${propertyId}`, { headers: this.headers });
        return plainToClass(SalePropertyDto, data);
    }

    async markForSale(propertyId: number, dto: CreateSalePropertyDto): Promise<SalePropertyDto> {
        const { data } = await axios.post(`${BASE}/sales/${propertyId}`, dto, { headers: this.headers });
        return plainToClass(SalePropertyDto, data);
    }

    async updateSaleProperty(propertyId: number, dto: Partial<CreateSalePropertyDto>): Promise<SalePropertyDto> {
        const { data } = await axios.put(`${BASE}/sales/${propertyId}`, dto, { headers: this.headers });
        return plainToClass(SalePropertyDto, data);
    }

    async removeFromSale(propertyId: number): Promise<void> {
        await axios.delete(`${BASE}/sales/${propertyId}`, { headers: this.headers });
    }

    async publishToML(propertyId: number, dto: PublishListingDto): Promise<SaleListingDto> {
        const { data } = await axios.post(`${BASE}/sales/${propertyId}/listings`, dto, { headers: this.headers });
        return plainToClass(SaleListingDto, data);
    }

    async pauseListing(listingId: number): Promise<SaleListingDto> {
        const { data } = await axios.put(`${BASE}/sales/listings/${listingId}/pause`, {}, { headers: this.headers });
        return plainToClass(SaleListingDto, data);
    }

    async closeListing(listingId: number): Promise<SaleListingDto> {
        const { data } = await axios.put(`${BASE}/sales/listings/${listingId}/close`, {}, { headers: this.headers });
        return plainToClass(SaleListingDto, data);
    }

    async syncListing(listingId: number): Promise<SaleListingDto> {
        const { data } = await axios.post(`${BASE}/sales/listings/${listingId}/sync`, {}, { headers: this.headers });
        return plainToClass(SaleListingDto, data);
    }

    async getMLStatus(): Promise<{ url: string; connected: boolean }> {
        const { data } = await axios.get(`${BASE}/sales/ml/auth-url`, { headers: this.headers });
        return data;
    }

    async disconnectML(): Promise<void> {
        await axios.delete(`${BASE}/sales/ml/disconnect`, { headers: this.headers });
    }

    async getMLStates(): Promise<MLNeighborhoodDto[]> {
        const { data } = await axios.get(`${BASE}/sales/ml/states`, { headers: this.headers });
        return data.map((item: any) => plainToClass(MLNeighborhoodDto, item));
    }

    async getMLCities(stateId: string): Promise<MLNeighborhoodDto[]> {
        const { data } = await axios.get(`${BASE}/sales/ml/states/${stateId}/cities`, { headers: this.headers });
        return data.map((item: any) => plainToClass(MLNeighborhoodDto, item));
    }

    async getMLNeighborhoods(cityId: string): Promise<MLNeighborhoodDto[]> {
        const { data } = await axios.get(`${BASE}/sales/ml/cities/${cityId}/neighborhoods`, { headers: this.headers });
        return data.map((item: any) => plainToClass(MLNeighborhoodDto, item));
    }

    async getPropertyPhotos(propertyId: number): Promise<PropertyPhotoDto[]> {
        const { data } = await axios.get(`${BASE}/properties/${propertyId}/photos`, { headers: this.headers });
        return data.map((item: any) => plainToClass(PropertyPhotoDto, item));
    }

    async uploadPropertyPhoto(propertyId: number, file: File): Promise<PropertyPhotoDto> {
        const formData = new FormData();
        formData.append('photo', file);
        const { data } = await axios.post(`${BASE}/properties/${propertyId}/photos`, formData, {
            headers: { ...this.headers, 'Content-Type': 'multipart/form-data' },
        });
        return plainToClass(PropertyPhotoDto, data);
    }

    async deletePropertyPhoto(propertyId: number, photoId: number): Promise<void> {
        await axios.delete(`${BASE}/properties/${propertyId}/photos/${photoId}`, { headers: this.headers });
    }
}
